import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  EmptyState,
  Input,
  ReportTable,
  type ReportTablePagination,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import {
  exportWoundStatusReportReportsWoundStatusExportGet,
  getWoundStatusReportOptionsReportsWoundStatusOptionsGet,
  getWoundStatusReportReportsWoundStatusGet,
} from '../services/api/sdk.gen';
import type {
  SortOrder,
  WoundStatusReportResult,
  WoundStatusReportRow,
  WoundStatusSortBy,
} from '../services/api/types.gen';
import { PermissionSlug } from '../constants/featureFlags';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { useDebounce } from '../hooks/useDebounce';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  coerceReportPageSize,
  formatReportEmptyValue,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';

const ALL_VALUE = '__all__';
const VALID_SORTS: WoundStatusSortBy[] = [
  'patient_name',
  'mrn',
  'clinic',
  'wound_number',
  'type',
  'location',
  'date_of_onset',
  'status',
  'progression_overall',
  'progression_last_30_days',
];
const VALID_DIRECTIONS: SortOrder[] = ['asc', 'desc'];

const columns = [
  {
    id: 'patient_name',
    header: 'Patient Name',
    accessor: 'patient_name' as const,
    render: (_value: unknown, row: WoundStatusReportRow) => (
      <Link
        className='font-medium text-primary underline-offset-2 hover:underline'
        to={`/patients/${row.patient_id}/wounds`}
      >
        {row.patient_name}
      </Link>
    ),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: WoundStatusReportRow) => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'clinic',
    header: 'Branch',
    accessor: (row: WoundStatusReportRow) => formatReportEmptyValue(row.clinic),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'wound_number',
    header: 'Wound #',
    accessor: 'wound_number' as const,
    enableSorting: true,
    minWidth: 100,
  },
  {
    id: 'type',
    header: 'Type',
    accessor: 'type' as const,
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'location',
    header: 'Location',
    accessor: 'location' as const,
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'date_of_onset',
    header: 'Date of Onset',
    accessor: 'date_of_onset_label' as const,
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'status_label' as const,
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'progression_overall',
    header: 'Progression (Overall)',
    accessor: (row: WoundStatusReportRow) => row.progression_overall.label,
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'progression_last_30_days',
    header: 'Progression (Last 30-Days)',
    accessor: (row: WoundStatusReportRow) => row.progression_last_30_days.label,
    enableSorting: true,
    minWidth: 220,
  },
];

type Option = { id: string; label: string };
type OptionsResponse = {
  clinics: Option[];
  statuses: Option[];
  types: Option[];
  progression: Option[];
};

function buildQuery(params: {
  clinic: string;
  status: string;
  type: string;
  progression: string;
  includeDischarged: boolean;
  q: string;
  sortBy?: WoundStatusSortBy;
  sortOrder?: SortOrder;
}) {
  return {
    clinic_id: params.clinic || null,
    statuses: params.status || null,
    types: params.type || null,
    progression: params.progression || null,
    include_discharged_patients: params.includeDischarged,
    q: params.q || null,
    sort_by: params.sortBy ?? null,
    sort_order: params.sortOrder ?? null,
  };
}

function FilterSelect({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: Option[];
  allLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className='space-y-1 text-sm font-medium'>
      {label}
      <Select
        value={value || ALL_VALUE}
        onValueChange={next => onChange(next === ALL_VALUE ? '' : next)}
      >
        <SelectTrigger selectSize='default'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>{allLabel}</SelectItem>
          {options.map(option => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

export default function WoundStatusReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const { hasPermission, showError } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      showError: state.showError,
    }))
  );

  const clinic = searchParams.get('clinic') ?? '';
  const status = searchParams.get('status') ?? '';
  const type = searchParams.get('type') ?? '';
  const progression = searchParams.get('progression') ?? '';
  const includeDischarged = searchParams.get('includeDischarged') === 'true';
  const q = searchParams.get('q') ?? '';
  const [searchText, setSearchText] = useState(q);
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(searchParams.get('sort'), VALID_SORTS);
  const sortOrder = readOptionalChoiceParam(
    searchParams.get('dir'),
    VALID_DIRECTIONS
  );

  const [report, setReport] = useState<WoundStatusReportResult | null>(null);
  const [options, setOptions] = useState<OptionsResponse>({
    clinics: [],
    statuses: [],
    types: [],
    progression: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const canAccess = hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS);

  const updateParams = useCallback(
    (updates: Record<string, string | number | boolean | null>) => {
      setSearchParams(current => {
        const params = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === false) {
            params.delete(key);
          } else {
            params.set(key, String(value));
          }
        });
        return params;
      });
    },
    [setSearchParams]
  );

  const debouncedUpdateSearch = useDebounce((value: string) => {
    updateParams({ q: value || null, page: 1 });
  }, 350);

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    getWoundStatusReportOptionsReportsWoundStatusOptionsGet()
      .then(response => {
        if (!cancelled) setOptions(response.data as OptionsResponse);
      })
      .catch(error => {
        console.error('Failed to load wound status report options', error);
        if (!cancelled)
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load report filters. Please try again.'
          );
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess, showError]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    setReport(null);
    getWoundStatusReportReportsWoundStatusGet({
      query: {
        ...buildQuery({
          clinic,
          status,
          type,
          progression,
          includeDischarged,
          q,
          sortBy,
          sortOrder,
        }),
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setLoadError(true);
          showError('Unable to load wound status report. Please try again.');
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load wound status report', error);
        if (!cancelled) {
          setLoadError(true);
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load wound status report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    canAccess,
    clinic,
    status,
    type,
    progression,
    includeDischarged,
    q,
    sortBy,
    sortOrder,
    page,
    pageSize,
    showError,
  ]);

  const sorting = useMemo<ReportTableSort | null>(
    () => (sortBy ? { id: sortBy, direction: sortOrder ?? 'asc' } : null),
    [sortBy, sortOrder]
  );
  const pagination = useMemo<ReportTablePagination>(
    () => ({
      page,
      pageSize,
      total: report?.total ?? 0,
      totalPages: Math.ceil((report?.total ?? 0) / pageSize),
      pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
    }),
    [page, pageSize, report?.total]
  );

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const response = await exportWoundStatusReportReportsWoundStatusExportGet(
        {
          query: buildQuery({
            clinic,
            status,
            type,
            progression,
            includeDischarged,
            q,
            sortBy,
            sortOrder,
          }),
          responseType: 'blob' as const,
        }
      );
      if (response.error || !response.data) {
        showError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download wound status report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'wound-status-report.xlsx');
    } catch (error) {
      console.error('Failed to download wound status report', error);
      showError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download wound status report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!canAccess) return <ReportsPermissionAlert />;

  const emptyState = loadError ? (
    <EmptyState title='Unable to load report' description='Please try again.' />
  ) : (
    <EmptyState
      title='No wounds found'
      description='Adjust filters and try again.'
    />
  );

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>Wound Status Report</h1>
          <p className='text-sm text-muted-foreground'>
            Review active and discontinued wound status by patient.
          </p>
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void exportReport()}
          disabled={isExporting || isLoading}
        >
          <Download className='h-4 w-4' /> Export
        </Button>
      </div>

      <Alert>
        <AlertDescription>
          Progression uses wound length and width measurements from Roger wound
          assessments.
        </AlertDescription>
      </Alert>

      <div className='grid gap-3 md:grid-cols-5'>
        <label className='space-y-1 text-sm font-medium md:col-span-2'>
          Search
          <Input
            type='search'
            inputSize='default'
            value={searchText}
            onChange={event => {
              const value = event.target.value;
              setSearchText(value);
              debouncedUpdateSearch(value);
            }}
            placeholder='Patient, MRN, or location'
          />
        </label>
        <FilterSelect
          label='Branch'
          value={clinic}
          options={options.clinics}
          allLabel='All branches'
          onChange={value => updateParams({ clinic: value, page: 1 })}
        />
        <FilterSelect
          label='Status'
          value={status}
          options={options.statuses}
          allLabel='All statuses'
          onChange={value => updateParams({ status: value, page: 1 })}
        />
        <FilterSelect
          label='Progression'
          value={progression}
          options={options.progression}
          allLabel='All progression'
          onChange={value => updateParams({ progression: value, page: 1 })}
        />
        <FilterSelect
          label='Type'
          value={type}
          options={options.types}
          allLabel='All types'
          onChange={value => updateParams({ type: value, page: 1 })}
        />
        <label className='flex items-center gap-2 pt-7 text-sm font-medium'>
          <Checkbox
            checked={includeDischarged}
            onCheckedChange={checked =>
              updateParams({ includeDischarged: checked === true, page: 1 })
            }
          />
          Include discharged patients
        </label>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Wound status report results'
        sorting={sorting}
        onSortingChange={next =>
          updateParams(
            next
              ? { sort: next.id, dir: next.direction, page: 1 }
              : { sort: null, dir: null, page: 1 }
          )
        }
        isLoading={isLoading}
        emptyState={emptyState}
        className='min-w-[1700px] text-xs'
        pagination={pagination}
        onPaginationChange={next =>
          updateParams({ page: next.page, size: next.pageSize })
        }
      />
    </div>
  );
}
